import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createCategory } from '@/lib/api';
import type { Category } from '@/types/firebase';
import { showNotification, showDuplicateEntryNotification } from '@/lib/generalNotifications';

interface AddCategoryDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  onCategoryCreated?: (category: Category) => void;
}

export function AddCategoryDialog({ isOpen, onOpenChange, companyId, onCategoryCreated }: AddCategoryDialogProps) {
  const [newCategoryName, setNewCategoryName] = useState('');
  const queryClient = useQueryClient();

  const createCategoryMutation = useMutation({
    mutationFn: async (category: Omit<Category, 'id' | 'createdAt'>) => {
      if (!category.name || !category.companyId) {
        throw new Error('Category name and company ID are required');
      }
      return createCategory(category);
    },
    onSuccess: (createdCategory: Category) => {
      // Call the callback with the created category FIRST, before dialog closes
      if (onCategoryCreated) {
        onCategoryCreated(createdCategory);
      }

      queryClient.invalidateQueries({ queryKey: ['categories', companyId] });
      showNotification({
        title: "Success",
        message: "Category added successfully",
        type: "success"
      });
      onOpenChange(false);
      setNewCategoryName('');
    },
    onError: (error: Error) => {
      if (error.message.includes('already exists')) {
        showDuplicateEntryNotification({
          entityType: 'category',
          name: newCategoryName.trim(),
          onEdit: () => {
            // Keep dialog open for editing
            onOpenChange(true);
          }
        });
      } else {
        showNotification({
          title: "Error",
          message: error.message,
          type: "error"
        });
      }
    }
  });

  const handleSubmit = () => {
    if (newCategoryName.trim()) {
      createCategoryMutation.mutate({
        name: newCategoryName.trim(),
        companyId,
        updatedAt: new Date()
      });
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    setNewCategoryName('');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add New Category</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="categoryName">Category Name</Label>
            <Input
              id="categoryName"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="Enter category name"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!newCategoryName.trim() || createCategoryMutation.isPending}
            >
              {createCategoryMutation.isPending ? 'Adding...' : 'Add Category'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}